/**
 * Monthly contact trend for the account, from the company-statistics history
 * mart (summed across the account's counted companies). UI mirrors the
 * proposal calculator's timeline: a filled recharts area in a DS Card with
 * summary badges, plus a dashed reference line at the contracted allotment so
 * you can see when usage crossed the contract. Contacts vs emails follows the
 * contract's pricing basis.
 */

import { useMemo } from "react";

import {
  Area,
  CartesianGrid,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { useContactHistory, type ExpansionAccount } from "../api";
import { EXPANSION_CHART_COLORS as C } from "../chartColors";
import { fmtContacts } from "../lib";
import { monthLabel } from "../../renewals/format";

const COLOR_BRAND = C.brand;
const COLOR_GRID = C.gridLine;
const COLOR_MARKER = C.marker; // the contracted-allotment reference line

/** Compact axis labels: 1.2M / 340K / 900. */
function compact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

export function ContactHistoryChart({ account }: { account: ExpansionAccount }) {
  const query = useContactHistory(account.account_id);
  const useEmail = account.uses_email_count;
  const label = useEmail ? "Emails" : "Contacts";

  const data = useMemo(
    () =>
      (query.data?.points ?? []).map((p) => ({
        month: p.month.slice(0, 7),
        value: useEmail ? p.emails : p.contacts,
      })),
    [query.data, useEmail],
  );

  const allotment = account.contracted_contacts;
  const peak = data.reduce((m, d) => Math.max(m, d.value), 0);
  // Keep the contract line in frame even when usage is far below/above it.
  const yMax = Math.max(peak, allotment ?? 0);

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
        <CardTitle className="text-sm">
          {label} over time ({data.length} mo)
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="neutral">Now {fmtContacts(account.usage)}</Badge>
          {allotment != null && (
            <Badge variant="neutral">Contracted {fmtContacts(allotment)}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-muted-foreground">
          Monthly {label.toLowerCase()} across the account&apos;s counted
          companies (source: company-statistics history). The dashed line is the
          contracted allotment — usage above it is billable overage.
        </p>
        {query.isLoading ? (
          <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
            Loading history…
          </div>
        ) : query.error ? (
          <div className="flex h-64 items-center justify-center text-sm text-destructive">
            Couldn&apos;t load contact history.
          </div>
        ) : data.length === 0 ? (
          <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
            No monthly history for this account yet.
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer>
              <ComposedChart data={data} margin={{ top: 10, right: 16, bottom: 4, left: 8 }}>
                <CartesianGrid stroke={COLOR_GRID} strokeDasharray="2 3" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 10 }}
                  interval="preserveStartEnd"
                  minTickGap={20}
                  tickFormatter={monthLabel}
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                  width={48}
                  domain={[0, Math.ceil(yMax * 1.05) || 1]}
                  tickFormatter={compact}
                />
                <Tooltip
                  formatter={(v) => [fmtContacts(Number(v)), label]}
                  labelFormatter={(m: string) => monthLabel(m)}
                />
                {allotment != null && allotment > 0 && (
                  <ReferenceLine
                    y={allotment}
                    stroke={COLOR_MARKER}
                    strokeDasharray="5 4"
                    label={{
                      value: "Contracted",
                      position: "insideTopRight",
                      fontSize: 10,
                      fill: COLOR_MARKER,
                    }}
                  />
                )}
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={COLOR_BRAND}
                  strokeWidth={2}
                  fill={COLOR_BRAND}
                  fillOpacity={0.12}
                  dot={false}
                  name={label}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
